// frontend/webSocketService.ts

// Remove 'RootState' from the import and ensure 'AppStore' is correctly imported
import { AppStore } from './store/store';
// Removed: import { RootState } from './store/store';

import { setSessionId } from './store/slices/sessionSlice';
import { receiveMessage, clearChatStatus, loadChatGroupMessages, resetChatSession, Message, setGenerating } from './store/slices/chatSlice';
import { addKbItemThunk, resetKbItemsThunk } from './store/slices/kbThunks';
// import { resetChatThunk } from './store/slices/resetChatThunks';
import { handlePdfArrivalThunk } from './store/slices/pdfThunks';
import { setChatGroups, addChatGroup, setChatMessages, setLoading, setError, addMessage, deleteChatGroup, setActiveChatGroup } from './store/slices/chatHistorySlice';
import { resetDocGen } from './store/slices/docGenSlice';
import { nanoid } from 'nanoid'; // Add import for nanoid
import { decrementProcessingCount } from './store/slices/fileProcessingSlice'; 
import { addPdf } from './store/slices/pdfSlice';
import ChatNavigationService from './services/ChatNavigationService';
import store from './store/store';
import { toggleRightSection } from './store/slices/sectionSlice';

// Add UUID v4 generator function
interface JsonChunk {
  method?: string;
  session_id?: string;
  operation?: string;
  status?: 'error' | 'success' | 'loading' | 'ready' | 'transcribing';
  doc_name?: string;
  error?: string;
  messageId?: string;
  chunk?: string;
  text?: string;
  model_name?: string;
  message?: string;
  original_filename?: string;
  file_type?: string;
  // Voice-specific fields
  transcribed_text?: string;
  user_id?: string;
  file_name?: string;
  // Phase 4: Chat history response types
  type?: string;
  chat_groups?: any[];
  chat_group_id?: string;
  messages?: any[];
  title?: string;
  // New backend message types
  user_name?: string;
  created_at?: string;
  success?: boolean;
  // Potential alternative field names the backend might use
  group_id?: string;
  chatGroupId?: string;
  group_title?: string;
  chatTitle?: string;
  id?: string;
  name?: string;
  // Direct chat access response data
  data?: {
    valid: boolean;
    error?: string;
    chat_group_id: string;
    chat_group: {
      id: string;
      title: string;
      user_id: string;
      created_at: string;
      updated_at: string;
    };
    messages: Array<{
      id: string;
      content: string;
      source: "keai" | "user";
      timestamp: string;
      session_id: string;
    }>;
  };
}

// Interface for outgoing WebSocket requests
interface WsRequest {
  method: string;
  params: Record<string, any>;  // Required field, even if empty
  messageId?: string;  // Optional field
}

class WebSocketService {
  private socket: WebSocket | null = null;
  private pingInterval: number | null = null;
  private reconnectAttempts = 0;
  private shouldReconnect = true;
  private store: AppStore;
  private getAccessTokenSilently: () => Promise<string>;
  private user: { sub: string; name: string } | undefined;
  private readonly SPECIAL_CHARACTER = '^';
  private readonly ALLOWED_EXTENSIONS = ['.pdf', '.json'];
  private session_id: string;
  private connectionPromise: Promise<void> | null = null;
  private connectionResolve: ((value: void | PromiseLike<void>) => void) | null = null;
  // Changed to POSITIVE_INFINITY to honor requirement of retrying until success
  private readonly MAX_RECONNECT_ATTEMPTS = Number.POSITIVE_INFINITY; // keep field to minimize unrelated changes
  private readonly RECONNECT_DELAY = 3000; // 3 seconds
  private isConnecting = false;
  // Added: track rejection and timers to avoid dangling timeouts and duplicate schedules
  private connectionReject: ((reason?: any) => void) | null = null; // ensures connect() can reject on timeout/close
  private connectionTimeoutId: number | null = null; // stores connect timeout id
  private reconnectTimeoutId: number | null = null; // deduplicate scheduled reconnects
  private readonly CONNECT_TIMEOUT_MS = 10000; // 10 seconds
  private onConnectionStatusChange: (isConnected: boolean) => void = () => {};

  constructor(
    store: AppStore,
    getAccessTokenSilently: () => Promise<string>,
    user: { sub: string; name: string } | undefined,
    session_id: string
  ) {
    this.store = store;
    this.getAccessTokenSilently = getAccessTokenSilently;
    this.user = user;
    this.session_id = session_id;
    this.connectionPromise = new Promise((resolve) => {
      this.connectionResolve = resolve;
    });
  }

  private constructWsUrl(token: string): string {
    const protocol = import.meta.env.VITE_WSS_ENABLED === 'true' ? 'wss' : 'ws';
    const host = import.meta.env.VITE_BACKEND_HOST || 'localhost';
    const port = import.meta.env.VITE_BACKEND_PORT || '8000';
    const baseUrl = import.meta.env.VITE_WS_URL || `${protocol}://${host}:${port}`;
    
    return `${baseUrl}/ws?token=${token}&userName=${encodeURIComponent(this.user?.name || '')}&sessionId=${this.session_id}`;
  }

  public async connect() {
    // Prevent multiple simultaneous connection attempts
    if (this.isConnecting) {
      console.log('Connection attempt already in progress');
      return;
    }

    // Check if already connected
    if (this.socket?.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    // Keep retrying indefinitely (MAX_RECONNECT_ATTEMPTS is Infinity)

    this.isConnecting = true;
    
    try {
      // Get access token
      const token = await this.getAccessTokenSilently();
      if (!token) {
        throw new Error('Failed to get access token');
      }

      // Construct WebSocket URL
      const wsUrl = this.constructWsUrl(token);
      
      // Close existing socket if any
      if (this.socket) {
        this.socket.close();
        this.socket = null;
      }

      this.shouldReconnect = true;
      console.log('Establishing WebSocket connection...');
      
      // Create new WebSocket connection
      this.socket = new WebSocket(wsUrl);
      this.setupSocketHandlers();
      
      // Reset connection promise with resolve & reject so we can fail fast on timeout or early close
      this.connectionPromise = new Promise((resolve, reject) => {
        this.connectionResolve = resolve;
        this.connectionReject = reject;
      });

      // Set connection timeout that rejects the promise (avoid throwing in setTimeout)
      if (this.connectionTimeoutId !== null) {
        clearTimeout(this.connectionTimeoutId);
        this.connectionTimeoutId = null;
      }
      this.connectionTimeoutId = window.setTimeout(() => {
        if (this.socket?.readyState !== WebSocket.OPEN) {
          try {
            this.socket?.close();
          } finally {
            // Reject the pending connection to unblock await in connect()
            if (this.connectionReject) {
              this.connectionReject(new Error('Connection timeout'));
              this.connectionReject = null;
            }
          }
        }
      }, this.CONNECT_TIMEOUT_MS);

      // Wait for connection to open or fail
      await this.connectionPromise;
      if (this.connectionTimeoutId !== null) {
        clearTimeout(this.connectionTimeoutId);
        this.connectionTimeoutId = null;
      }
      // Clear reject on successful connection to avoid accidental reuse
      this.connectionReject = null;
      
    } catch (error) {
      console.error('Failed to establish WebSocket connection:', error);
      // Schedule a reconnect attempt if applicable (deduplicated)
      if (this.shouldReconnect) {
        this.scheduleReconnect();
      }
      // Do not rethrow to avoid bubbling uncaught errors during auto-reconnect loops
    } finally {
      this.isConnecting = false;
    }
  }

  private setupSocketHandlers() {
    if (!this.socket) return;

    this.socket.onopen = () => {
      console.log('WebSocket connected successfully');
      this.reconnectAttempts = 0;
      this.setupPingInterval();
      // On successful open, clear any pending connect timeout
      if (this.connectionTimeoutId !== null) {
        clearTimeout(this.connectionTimeoutId);
        this.connectionTimeoutId = null;
      }
      // Resolve pending connection promise
      if (this.connectionResolve) {
        this.connectionResolve();
        this.connectionResolve = null;
      }
      // Clear reject in case it exists
      this.connectionReject = null;
      // Notify listeners
      this.onConnectionStatusChange(true);
    };

    this.socket.onmessage = this.handleMessage.bind(this);
    this.socket.onerror = (error: Event) => {
      console.error('WebSocket error occurred:', error);
      // If we are in the middle of connecting and an error occurs, reject the pending promise
      if (this.connectionReject) {
        this.connectionReject(error);
        this.connectionReject = null;
      }
    };
    this.socket.onclose = this.handleClose.bind(this);
  }

  public async waitForConnection(): Promise<void> {
    if (!this.connectionPromise) {
      throw new Error('WebSocket not initialized');
    }
    return this.connectionPromise;
  }

  public disconnect() {
    this.shouldReconnect = false;
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    // Clear any pending reconnect attempts
    if (this.reconnectTimeoutId !== null) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
    // Clear any pending connect timeout and reject promise to unblock waiters
    if (this.connectionTimeoutId !== null) {
      clearTimeout(this.connectionTimeoutId);
      this.connectionTimeoutId = null;
    }
    if (this.connectionReject) {
      this.connectionReject(new Error('Disconnected'));
      this.connectionReject = null;
    }
    if (this.pingInterval !== null) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    // Reset docGen state in Redux
    this.store.dispatch(resetDocGen());
    // Notify listeners
    this.onConnectionStatusChange(false);
  }

  private setupPingInterval() {
    this.pingInterval = window.setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ 
          method: 'ping',
          params: {}  // Add required empty params object
        }));
      }
    }, 30000); // Ping every 30 seconds
  }

  private async handleMessage(event: MessageEvent) {
    try {
      const rawData = event.data;
      
      const jsonChunk: JsonChunk = JSON.parse(rawData);
      if (jsonChunk.error) {
        console.error('Backend error received:', jsonChunk.error);
        return;
      }

      // Handle session_id first
      if (jsonChunk.session_id) {
        console.log('Setting session_id in Redux store:', jsonChunk.session_id);
        this.store.dispatch(setSessionId(jsonChunk.session_id));
        return;
      }

      if (jsonChunk.method === 'pong') {
        return;
      }

      // Handle voice-specific messages
      if (jsonChunk.method === 'voice_status' || 
          jsonChunk.method === 'voice_transcription' || 
          jsonChunk.method === 'voice_error') {
        console.log('🎤 WebSocket: Received voice message:', {
          method: jsonChunk.method,
          status: jsonChunk.status,
          transcribed_text: jsonChunk.transcribed_text,
          message: jsonChunk.message,
          user_id: jsonChunk.user_id
        });
        
        // Voice messages are handled directly by the VoiceButton component
        // via custom event dispatching
        console.log('🎤 WebSocket: Dispatching voice-message event');
        window.dispatchEvent(new MessageEvent('voice-message', { data: event.data }));
        return;
      }

      if (jsonChunk.operation) {
        await this.handleOperation(jsonChunk);
        return;
      }

      // Handle AI response chunks first
      if (jsonChunk.chunk || jsonChunk.text) {
        await this.handleIncomingChunk(jsonChunk);
        return;
      }

      // Phase 4: Handle chat history responses
      switch (jsonChunk.type) {
        case 'user_initialized':
          this.handleUserInitialized(jsonChunk);
          break;

        case 'new_blank_chat':
          this.handleNewBlankChat(jsonChunk);
          break;

        case 'new_chat_group':
          this.handleNewChatGroup(jsonChunk);
          break;

        case 'chat_switched':
        case 'direct_chat_access':
          this.handleDirectChatAccess(jsonChunk);
          break;

        case 'chat_groups':
          this.handleChatGroups(jsonChunk);
          break;

        case 'chat_group':
          this.handleNewChatGroup(jsonChunk);
          break;

        case 'chat_message':
        case 'chat_messages':
          this.handleChatMessages(jsonChunk);
          break;

        case 'chat_group_deleted':
          this.handleChatGroupDeleted(jsonChunk);
          break;

        default:
          console.log(`Unhandled message with type "${jsonChunk.type}" and method "${jsonChunk.method}":`, jsonChunk);
      }
    } catch (error) {
      console.error('Error handling WebSocket message:', error);
    }
  }

  private async handleIncomingChunk(jsonChunk: JsonChunk) {
    const text = jsonChunk.text || jsonChunk.chunk || '';

    if (text.trim() === '') {
      return;
    }

    // Regular expression to match markdown links with the filename
    const pdfLinkRegex = /\[.*?\]\(\^(.+?\.pdf)\)/;
    const match = text.match(pdfLinkRegex);
    
    let fileName = '';

    if (match && match[1]) {
      // Extract filename from markdown link
      fileName = match[1];
      console.log('PDF link detected in markdown format:', fileName);
    } else if (
      text.includes(this.SPECIAL_CHARACTER) &&
      this.ALLOWED_EXTENSIONS.some(ext => text.toLowerCase().includes(ext))
    ) {
      // Extract filename from text containing '^' and ending with '.pdf' or '.json'
      const specialCharIndex = text.indexOf(this.SPECIAL_CHARACTER);
      
      // Find which extension is present
      const extension = this.ALLOWED_EXTENSIONS.find(ext => text.toLowerCase().includes(ext));
      
      if (extension) {
        const extensionIndex = text.toLowerCase().indexOf(extension, specialCharIndex);
        if (extensionIndex !== -1) {
          // Start from the character after '^'
          fileName = text.substring(specialCharIndex + 1, extensionIndex + extension.length).trim();
          console.log(`${extension.toUpperCase()} filename extracted from text:`, fileName);
        }
      }
    }

    if (fileName) {
      // Handle PDF arrival
      try {
        console.log('Dispatching handlePdfArrivalThunk for:', fileName);
        await this.store.dispatch(handlePdfArrivalThunk(fileName));

        // Decrement processing count
        this.store.dispatch(decrementProcessingCount());
        console.log('webSocketService: Processing count decremented after PDF arrival');

        // Send completion message after PDF is handled
        const messageId = jsonChunk.messageId || nanoid();
        this.store.dispatch(
          receiveMessage({
            source: 'keai',
            text: `The requested report is now generated. You can find the document titled: ${fileName} in the right section.`,
            messageId: messageId,
            timestamp: new Date().toISOString()
          })
        );
      } catch (error) {
        console.error('Error handling PDF arrival:', error);
        // Ensure processing count is decremented on error
        this.store.dispatch(decrementProcessingCount());
      }
      // Reset docGen state after handling
      this.store.dispatch(resetDocGen());

      // Clear chatStatus when the PDF arrives
      this.store.dispatch(clearChatStatus());

        // Stop AI typing animation when document has arrived
        this.store.dispatch(setGenerating(false));

      return; // Return here to prevent the text from being added as a regular message
    }

    // Only add as regular message if it's not a PDF file notification
    if (!text.includes(this.SPECIAL_CHARACTER)) {
      const messageId = jsonChunk.messageId || nanoid();
      const aiMessage = {
        source: 'keai' as const,
        text: text,
        messageId: messageId,
        timestamp: new Date().toISOString()
      };

      // Store AI response in main chat view
      this.store.dispatch(receiveMessage(aiMessage));

      // Also store AI response in chat history for persistence
      const currentState = this.store.getState();
      const activeChatGroup = currentState.chatHistory?.activeChatGroup;
      
      // If no active chat group, let backend create one
      if (!activeChatGroup) {
        console.log('No active chat group for AI response, waiting for backend to create one');
      } else {
        // Add message to existing chat group
        this.store.dispatch(addMessage({
          chatGroupId: activeChatGroup,
          message: {
            id: messageId,
            content: text,
            source: 'keai',
            timestamp: new Date().toISOString(),
            session_id: currentState.session?.session_id || undefined
          }
        }));
        
      }
      
      // Request fresh chat groups list to ensure UI is updated with latest timestamps
      this.getChatGroups();
    }

    // Clear chatStatus when assistant's reply is received
    this.store.dispatch(setGenerating(false));
    this.store.dispatch(clearChatStatus());
  }

  private async handleOperation(jsonChunk: JsonChunk): Promise<void> {
    switch (jsonChunk.operation) {
      case 'file_load':
      case 'url_load':
        await this.handleFileLoad(jsonChunk);
        break;
      case 'clear_kb':
        this.handleClearKb(jsonChunk);
        break;
      case 'set_model':
        this.handleSetModel(jsonChunk);
        break;
      case 'file_regenerate':
        this.handleFileRegenerate(jsonChunk);
        break;
      default:
        break;
    }
  }

  private async handleFileLoad(jsonChunk: JsonChunk): Promise<void> {
    try {
      const docName = jsonChunk.doc_name || '';
      const operation = jsonChunk.operation || '';
      
      // Determine file type based on filename
      let type: 'pdf' | 'url' | 'txt' | 'docx' | 'csv' | 'json' | undefined;
      if (operation === 'url_load') {
        type = 'url';
      } else {
        const extension = docName.split('.').pop()?.toLowerCase();
        switch (extension) {
          case 'pdf':
            type = 'pdf';
            break;
          case 'txt':
            type = 'txt';
            break;
          case 'docx':
            type = 'docx';
            break;
          case 'csv':
            type = 'csv';
            break;
          case 'json':
            type = 'json';
            break;
        }
      }

      await this.store.dispatch(
        addKbItemThunk({
          fileName: docName,
          type,
          status: (jsonChunk.status === 'success' || jsonChunk.status === 'error') ? jsonChunk.status : 'error',
          errMessage: jsonChunk.error || '',
        })
      );

      // Decrement processing count
      this.store.dispatch(decrementProcessingCount());
      console.log('webSocketService: Processing count decremented');
    } catch (error) {
      console.error('Error loading file:', error);
      this.store.dispatch(decrementProcessingCount());
      console.log('webSocketService: Processing count decremented after error');
    }
  }

  private handleClearKb(jsonChunk: JsonChunk): void {
    if (jsonChunk.status === 'success') {
      this.store.dispatch(resetKbItemsThunk());
    } else if (jsonChunk.status === 'error') {
      console.error(`Error clearing knowledge base: ${jsonChunk.message}`);
    }
  }

  private handleSetModel(jsonChunk: JsonChunk): void {
    if (jsonChunk.status === 'success') {
      console.log(`Model set successfully: ${jsonChunk.model_name}`);
    } else if (jsonChunk.status === 'error') {
      console.error(`Error setting model: ${jsonChunk.message}`);
    }
  }

  private handleFileRegenerate(jsonChunk: JsonChunk): void {
    console.log('Handling file regenerate response:', jsonChunk);
    
    if (jsonChunk.status === 'error') {
      console.error(`File regeneration error: ${jsonChunk.message}`);
      
      // Dispatch error message to show user
      const message: Message = {
        messageId: nanoid(),
        text: `Document regeneration failed: ${jsonChunk.message || 'Unknown error'}`,
        timestamp: new Date().toISOString(),
        source: 'keai',
      };
      
      store.dispatch(receiveMessage(message));
      return;
    }

    if (jsonChunk.status === 'success') {
      console.log('File regeneration completed successfully');
      
      // Dispatch success message to show user
      const message: Message = {
        messageId: nanoid(),
        text: `Document regenerated successfully! ${jsonChunk.file_name ? `New file: ${jsonChunk.file_name}` : ''}`,
        timestamp: new Date().toISOString(),
        source: 'keai',
      };
      
      store.dispatch(receiveMessage(message));
      
      // If a new file was generated, handle it
      if (jsonChunk.file_name) {
        // Check if it's a PDF file (starts with '^')
        if (jsonChunk.file_name.startsWith('^')) {
          const pdfFilename = jsonChunk.file_name.substring(1); // Remove '^' prefix
          console.log('New PDF generated:', pdfFilename);
          
          // Dispatch PDF arrival
          store.dispatch(handlePdfArrivalThunk(pdfFilename));
        }
      }
      
      return;
    }

    // Handle any other status
    console.log(`File regenerate status: ${jsonChunk.status}, message: ${jsonChunk.message || 'No message'}`);
  }

  // Phase 4: Chat history message handlers
  private handleUserInitialized(jsonChunk: JsonChunk): void {
    console.log('👤 User initialized:', jsonChunk);
    if (jsonChunk.chat_groups) {
      // First dispatch chat groups to store
      this.store.dispatch(setChatGroups(jsonChunk.chat_groups));
      
      // Get current URL parameters to check for chat_id
      const urlParams = new URLSearchParams(window.location.search);
      const chatIdFromUrl = urlParams.get('chat_id');
      
      // Get current state
      const currentState = this.store.getState();
      const { activeChatGroup, chatGroups } = currentState.chatHistory;
      
      // If URL has chat_id and it exists in our groups, use that
      if (chatIdFromUrl && chatGroups.some(group => group.id === chatIdFromUrl)) {
        this.store.dispatch(setActiveChatGroup(chatIdFromUrl));
      }
      // Otherwise if no active group is set, use most recent
      else if (!activeChatGroup && chatGroups.length > 0) {
        const mostRecentGroup = chatGroups[0]; // Already sorted by updated_at
        this.store.dispatch(setActiveChatGroup(mostRecentGroup.id));
        
        // Update URL to reflect the active chat
        const chatNavService = ChatNavigationService.getInstance();
        chatNavService.updateURL(mostRecentGroup.id);
      }
      
      console.log('✅ Chat groups loaded and active chat set on user initialization');
    }
  }

  private handleNewBlankChat(jsonChunk: JsonChunk): void {
    console.log('📝 New blank chat created:', jsonChunk);
    if (jsonChunk.chat_group_id && jsonChunk.title) {
      const newGroup = {
        id: jsonChunk.chat_group_id,
        title: jsonChunk.title || 'New Chat',
        user_id: this.user?.sub || '',
        created_at: jsonChunk.created_at || new Date().toISOString(),
        updated_at: jsonChunk.created_at || new Date().toISOString(),
        messages: [], // Initialize empty messages array
      };
      
      // Update URL to reflect new chat first
      const chatNavService = ChatNavigationService.getInstance();
      chatNavService.updateURL(jsonChunk.chat_group_id);
      
      // Update Redux store with new chat
      this.store.dispatch(addChatGroup(newGroup));
      this.store.dispatch(setActiveChatGroup(jsonChunk.chat_group_id));
      
      // Reset chat session for new chat
      this.store.dispatch(setSessionId(jsonChunk.chat_group_id));
      
      // Request fresh chat groups list to ensure UI is updated
      this.getChatGroups();
      
      console.log('✅ New blank chat added to store, URL updated, and set as active');
    }
  }

  private handleDirectChatAccess(jsonChunk: JsonChunk) {
    if (!jsonChunk.data?.valid) {
      console.error('Invalid chat access:', jsonChunk.data?.error);
      return;
    }

    const { chat_group_id, chat_group, messages } = jsonChunk.data;

    // Set the active chat group
    this.store.dispatch(setActiveChatGroup(chat_group_id));

    // Set the messages for this chat group
    if (messages) {
      // Update messages in chatHistorySlice
      this.store.dispatch(setChatMessages({ chatGroupId: chat_group_id, messages }));
      
      // Update messages in chatSlice for display
      const formattedMessages = messages.map(msg => ({
        messageId: msg.id,
        text: msg.content,
        source: msg.source as 'user' | 'keai',
        timestamp: msg.timestamp,
        session_id: msg.session_id
      }));
      this.store.dispatch(loadChatGroupMessages(formattedMessages));
    }
  }

  private handleChatGroups(jsonChunk: JsonChunk): void {
    console.log('Received chat groups:', jsonChunk.chat_groups);
    if (jsonChunk.chat_groups) {
      // Remove quotes from titles before storing in Redux
      const formattedGroups = jsonChunk.chat_groups.map(group => ({
        ...group,
        title: group.title.replace(/^"|"$/g, '') // Remove leading and trailing quotes
      }));
      this.store.dispatch(setChatGroups(formattedGroups));
    }
  }

  private handleChatGroupDeleted(jsonChunk: JsonChunk): void {
    console.log('Chat group deleted:', jsonChunk);
    if (jsonChunk.chat_group_id && jsonChunk.success) {
      this.store.dispatch(deleteChatGroup(jsonChunk.chat_group_id));
      console.log('Chat group removed from store');
      
      // If this was the active chat group, navigate to a new one
      const currentState = this.store.getState();
      if (currentState.chatHistory.activeChatGroup === jsonChunk.chat_group_id) {
        // Get the first available chat group
        const firstGroup = currentState.chatHistory.chatGroups[0];
        if (firstGroup) {
          // Navigate to the first available chat
          const chatNavService = ChatNavigationService.getInstance();
          chatNavService.updateURL(firstGroup.id);
          this.store.dispatch(setActiveChatGroup(firstGroup.id));
        } else {
          // No chats left, clear URL and reset chat
          const chatNavService = ChatNavigationService.getInstance();
          chatNavService.updateURL('');
          this.store.dispatch(resetChatSession());
        }
      }
    }
  }

  private handleChatMessages(jsonChunk: JsonChunk): void {
    if (jsonChunk.messages) {
      const transformedMessages = jsonChunk.messages.map(transformMessage);
      this.store.dispatch(loadChatGroupMessages(transformedMessages));
      
      // Also update messages in chat history
      if (jsonChunk.chat_group_id) {
        this.store.dispatch(setChatMessages({
          chatGroupId: jsonChunk.chat_group_id,
          messages: jsonChunk.messages
        }));
      }
    }
  }

  private handleNewChatGroup(jsonChunk: JsonChunk): void {
    console.log('Processing potential new chat group message');
    console.log('Full message data:', JSON.stringify(jsonChunk, null, 2));
    
    // Extract chat group ID from various possible field names
    const chatGroupId = jsonChunk.chat_group_id || jsonChunk.group_id || jsonChunk.chatGroupId || jsonChunk.id;
    
    // Try to extract title from various possible field names  
    const title = (jsonChunk.title || jsonChunk.group_title || jsonChunk.chatTitle || jsonChunk.name || '').replace(/^"|"$/g, '');
    
    console.log('Extracted values:', { chatGroupId, title });
    
    if (chatGroupId && title) {
      const newGroup = {
        id: chatGroupId,
        title: title,
        user_id: this.user?.sub || '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      console.log('Adding to Redux store:', newGroup);
      
      // Check if this chat group already exists
      const currentState = this.store.getState();
      const existingGroup = currentState.chatHistory?.chatGroups.find(group => group.id === chatGroupId);
      
      if (!existingGroup) {
        // Only update URL and store if this is a new group
        const chatNavService = ChatNavigationService.getInstance();
        chatNavService.updateURL(chatGroupId);
        
        this.store.dispatch(addChatGroup(newGroup));
        this.store.dispatch(setActiveChatGroup(chatGroupId));
        
        // Request messages for this chat group to ensure they're loaded
        this.getChatMessages(chatGroupId);
        
        // Refresh chat groups to ensure UI is updated
        this.getChatGroups();
        
        console.log('New chat group successfully added to store and activated');
      } else {
        console.log('Chat group already exists, skipping store update');
      }
    } else {
      console.error('Missing required fields:', { 
        availableFields: Object.keys(jsonChunk),
        chatGroupId, 
        title,
        rawMessage: jsonChunk
      });
    }
  }

  public sendMessage(message: string) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(message);
    } else {
      console.error('WebSocket is not connected. Cannot send message.');
    }
  }

  public sendBinaryMessage(binaryData: Blob | ArrayBuffer | ArrayBufferView) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(binaryData);
    } else {
      console.error('WebSocket is not open.');
    }
  }

  private handleClose() {
    console.log('WebSocket connection closed');
    if (this.pingInterval !== null) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    // If a connection attempt was pending, reject to unblock await in connect()
    if (this.connectionReject) {
      this.connectionReject(new Error('Connection closed'));
      this.connectionReject = null;
    }
    // Notify listeners
    this.onConnectionStatusChange(false);
    if (this.shouldReconnect) {
      this.scheduleReconnect();
    }
  }

  // Added: centralized, deduplicated reconnect scheduling
  private scheduleReconnect() {
    if (!this.shouldReconnect) return;
    if (this.reconnectTimeoutId !== null) return; // already scheduled
    console.log(`Reconnecting in ${this.RECONNECT_DELAY}ms... (Attempt ${this.reconnectAttempts + 1})`);
    this.reconnectTimeoutId = window.setTimeout(() => {
      this.reconnectTimeoutId = null;
      this.reconnectAttempts += 1;
      // Guard against parallel connects
      if (!this.isConnecting) {
        this.connect().catch(() => {
          // Swallow errors here; scheduleReconnect will handle future attempts
        });
      }
    }, this.RECONNECT_DELAY);
  }

  // Added: allow external listeners (e.g., React context) to subscribe to connection status
  public setConnectionStatusListener(listener: (isConnected: boolean) => void) {
    this.onConnectionStatusChange = listener || (() => {});
  }

  // Phase 4: Chat History Integration Methods
  public getChatGroups() {
    if (this.socket?.readyState === WebSocket.OPEN) {
      const request = {
        method: 'get_chat_groups',
        params: {}
      };
      this.socket.send(JSON.stringify(request));
    } else {
      console.error('WebSocket is not connected. Cannot get chat groups.');
    }
  }

  public getChatMessages(chatGroupId: string) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      const request = {
        method: 'get_chat_messages',
        params: { chat_group_id: chatGroupId }
      };
      this.socket.send(JSON.stringify(request));
    } else {
      console.error('WebSocket is not connected. Cannot get chat messages.');
    }
  }

  public deleteChatGroup(chatGroupId: string) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      const request = {
        method: 'delete_chat_group',
        params: { chat_group_id: chatGroupId }
      };
      this.socket.send(JSON.stringify(request));
    } else {
      console.error('WebSocket is not connected. Cannot delete chat group.');
    }
  }

  // New methods for enhanced chat functionality
  public initializeUser() {
    if (this.socket?.readyState === WebSocket.OPEN) {
      const request = {
        method: 'initialize_user',
        params: {}
      };
      this.socket.send(JSON.stringify(request));
      console.log('Initializing user and loading chat groups');
    } else {
      console.error('WebSocket is not connected. Cannot initialize user.');
    }
  }

  public createNewChat() {
    if (this.socket?.readyState === WebSocket.OPEN) {
      // Reset chat session for new chat
      this.store.dispatch(resetChatSession());
      
      // Let backend create the chat group
      const request = {
        method: 'chat',
        params: {
          message: '',
          session_id: this.session_id,
          model_name: 'gpt-5',
          stream: true
          // No chat_group_id - let backend create it
        }
      };
      this.socket.send(JSON.stringify(request));
      console.log('Creating new chat via backend');
    } else {
      console.error('WebSocket is not connected. Cannot create new chat.');
    }
  }

  public switchToChat(chatGroupId: string) {
    // First check if the chat has any PDFs
    const state = this.store.getState();
    const chatPdfs = state.pdf.filesByChat[chatGroupId] || [];
    
    // If chat has PDFs, open right section
    if (chatPdfs.length > 0) {
      this.store.dispatch(toggleRightSection(true));
    } else {
      this.store.dispatch(toggleRightSection(false));
    }
    
    // Then send the request to get chat messages
    const request = JSON.stringify({
      method: 'get_chat_messages',
      params: {
        chat_group_id: chatGroupId
      }
    });
    this.sendMessage(request);
  }
}

// Transform WebSocket message to store format
const transformMessage = (wsMessage: any) => {
  return {
    messageId: wsMessage.id,
    text: wsMessage.content,
    source: wsMessage.source,
    timestamp: wsMessage.timestamp,
    session_id: wsMessage.session_id
  };
};

// Handle chat message event
const handleChatMessage = (data: any, dispatch: any) => {
  if (data.type === 'chat_message' && data.messages) {
    const transformedMessages = data.messages.map(transformMessage);
    dispatch(loadChatGroupMessages(transformedMessages));
  }
};

export { WebSocketService };